import { Component, Injector, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ApComplexModal } from 'webcommon/core-ui';
import { FcItemModalContext, FcItemModalMode } from '../../../models';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { FcListValidationService } from '../../generic';
import { FormGroupValidationRules, TimeFrameTypeDto } from 'webcommon/shared';
import { FindControlIdService } from 'webcommon/legacy-common';

@Component({
  providers: [FcListValidationService],
  selector: 'fc-employment-list-modal',
  templateUrl: './fc-employment-list-modal.component.html',
  styleUrls: ['./fc-employment-list-modal.component.less']
})
export class FcEmploymentListModalComponent extends ApComplexModal<any> implements OnInit, OnChanges {
  public baseId = 'fc-employment-list-modal';
  public visitId: string;
  public patientId: string;
  public isDisabled = false;
  public listItemName = 'Employment';
  public isSubmitted = false;
  public itemContext: FcItemModalContext;
  public readonly findControlIdService: FindControlIdService;
  public readonly FindControlIdService = FindControlIdService;

  public fcItemModalModeEnum = FcItemModalMode;

  public mode: number;
  public item: any;
  public itemForModify: any;
  public fullName: string;

  public listItem: any = {
    Department: '',
  };

  public employerPks: string[] = [];
  public employmentStatusPks: string[] = [];
  public occupationPks: string[] = [];
  public industryPks: string[] = [];

  public employerUidControl: FormControl =  new FormControl<string>('');
  public employerNameControl: FormControl =  new FormControl<string>('');
  public employmentStatusUidControl: FormControl =  new FormControl<string>('');
  public employmentStatusNameControl: FormControl =  new FormControl<string>('');
  public occupationUidControl: FormControl =  new FormControl<string>('');
  public occupationNameControl: FormControl =  new FormControl<string>('');
  public industryUidControl: FormControl =  new FormControl<string>('');
  public industryNameControl: FormControl =  new FormControl<string>('');
  public departmentControl: FormControl =  new FormControl<string>('');
  public startDateControl: FormControl = new FormControl<Date | null>(null);
  public endDateControl: FormControl = new FormControl<Date | null>(null);
  public idControl: FormControl = new FormControl<number>(new Date().getTime());
  public form: FormGroup = new FormGroup({
    Department: this.departmentControl,
    StartDate: this.startDateControl,
    EndDate: this.endDateControl,
    Id: this.idControl,
    EmployerName: this.employerNameControl,
    EmploymentStatusName: this.employmentStatusNameControl,
    OccupationName: this.occupationNameControl,
    IndustryName: this.industryNameControl,
    EmployerUid: this.employerUidControl,
    EmploymentStatusUid: this.employmentStatusUidControl,
    IndustryUid: this.industryUidControl,
    OccupationUid: this.occupationUidControl,
  });

  public listItemDefaultValidation: FormGroupValidationRules = {
    Department: {
      MaxLength: 50,
    }
  };

  public startTimeFrame = new TimeFrameTypeDto();
  public endTimeFrame = new TimeFrameTypeDto();
  public startTimeframeChangeInProgress = false;
  public endTimeframeChangeInProgress = false;

  constructor(
    injector: Injector,
    public readonly fcListValidation: FcListValidationService) {
    super(injector);
    this.findControlIdService = injector.get(FindControlIdService);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
    }
    if (changes.form != null) {
      this.addControlsToForm();
    }
    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public override ngOnInit() {
    super.ngOnInit();

    if (this.mode === FcItemModalMode.Modify) {
      this.listItemName = `Employment - ${this.fullName}`;
      this.employerPks.push(this.parseFindControlValue(this.itemForModify.EmployerUid));
      this.employmentStatusPks.push(this.parseFindControlValue(this.itemForModify.EmploymentStatusUid));
      this.occupationPks.push(this.parseFindControlValue(this.itemForModify.OccupationUid));
      this.industryPks.push(this.parseFindControlValue(this.itemForModify.IndustryUid));
      this.departmentControl.setValue(this.itemForModify.Department);
      this.startDateControl.setValue(this.itemForModify?.StartDate);
      this.endDateControl.setValue(this.itemForModify?.EndDate);
      this.idControl.setValue(this.itemForModify.Id);
    }
  }

  public onDepartmentChange(): void {
    if (!this.departmentControl.value) {
      this.departmentControl.markAsPristine();
    }
  }

  public onStartTimeFrameChanged(dto: TimeFrameTypeDto): void {
    this.startTimeFrame = dto;
    this.startDateControl.setValue(dto);
  }

  public onEndTimeFrameChanged(dto: TimeFrameTypeDto): void {
    this.endTimeFrame = dto;
    this.endDateControl.setValue(dto);
  }

  public onStartDateTimeframeChangeInprogressChanged(value: boolean) {
    this.startTimeframeChangeInProgress = value;
  }

  public onEndDateTimeframeChangeInprogressChanged(value: boolean) {
    this.endTimeframeChangeInProgress = value;
  }

  public getFindControlValidation(controlName: string): any {
    if (this.fcListValidation.getValidators(controlName).length > 0) {
      return this.fcListValidation.getValidators(controlName);
    } else {
      return this.listItemDefaultValidation[controlName]?.IsRequired;
    }
  }

  public onEmployerChanged(employer: any): void {
    if (employer.length) {
      this.employerNameControl.setValue(employer[0].Name);
      this.employerUidControl.setValue(employer[0].Pk);
    } else {
      this.employerNameControl.reset();
      this.employerUidControl.reset();
    }
  }

  public onEmploymentChanged(employment: any): void {
    if (employment.length) {
      this.employmentStatusNameControl.setValue(employment[0].Name);
      this.employmentStatusUidControl.setValue(employment[0].Pk);
    } else {
      this.employmentStatusNameControl.reset();
      this.employmentStatusUidControl.reset();
    }
  }

  public onOccupationChanged(occupation: any): void {
    if (occupation.length) {
      this.occupationNameControl.setValue(occupation[0].Name);
      this.occupationUidControl.setValue(occupation[0].Pk);
    } else {
      this.occupationNameControl.reset();
      this.occupationUidControl.reset();
    }
  }

  public onIndustryChanged(industry: any): void {
    if (industry.length) {
      this.industryNameControl.setValue(industry[0].Name);
      this.industryUidControl.setValue(industry[0].Pk);
    } else {
      this.industryNameControl.reset();
      this.industryUidControl.reset();
    }
  }

  public showErrors(control: AbstractControl, isSubmitted: boolean = false): boolean {
    return FcListValidationService.showErrors(control, isSubmitted);
  }

  public isRequired(control: FormControl): boolean {
    return control.hasValidator(Validators.required);
  }

  public onSave(): void {
    this.apClose({
      result: this.form.getRawValue()
    });
  }

  public onCancel(): void {
    this.apClose({result: false});
  }

  public getFindControlValue(value: string | string[]): string[] {
    const newSelectedPks: string[] = Array.isArray(value)
      ? [...value.filter(pk => typeof pk === 'string' && pk.length > 0)]
      : typeof value === 'string' && value.length > 0
      ? [value]
      : [];
    return newSelectedPks;
  }

  public parseFindControlValue(value: string | string[], multiselect?: false): string;
  public parseFindControlValue(value: string | string[], multiselect?: true): string[];
  public parseFindControlValue(value: string | string[], multiselect: boolean = false): string | string[] {
    if (Array.isArray(value) && value.length > 0) {
      return multiselect ? value : value[0];
    } else if (typeof value === 'string' && value.length > 0) {
      return multiselect ? [value] : value;
    }
    return multiselect ? [] : '';
  }

  private addControlsToForm(): void {
    this.form.addControl('EmployerUid', this.employerUidControl);
    this.form.addControl('EmploymentStatusUid', this.employmentStatusUidControl);
    this.form.addControl('OccupationUid', this.occupationUidControl);
    this.form.addControl('IndustryUid', this.industryUidControl);
    this.form.addControl('Department', this.departmentControl);
    this.form.addControl('StartDate', this.startDateControl);
    this.form.addControl('EndDate', this.endDateControl);
    this.form.addControl('Id', this.idControl);
    this.form.addControl('EmployerName', this.employerNameControl);
    this.form.addControl('EmploymentStatusName', this.employmentStatusNameControl);
    this.form.addControl('OccupationName', this.occupationNameControl);
    this.form.addControl('IndustryName', this.industryNameControl);
  }

  private setForm(): void {
    this.employerPks = this.getFindControlValue(this.listItem?.EmployerUid);
    this.employmentStatusPks = this.getFindControlValue(this.listItem?.EmploymentStatusUid);
    this.occupationPks = this.getFindControlValue(this.listItem?.OccupationUid);
    this.industryPks = this.getFindControlValue(this.listItem?.IndustryUid);
    this.departmentControl.setValidators(this.fcListValidation.getValidators('Department'));
    this.departmentControl.setValue(this.listItem?.Department);
  }
}
